/**
 * 知识库智能问答面板（P0 基础 + P1 反馈闭环/规则直查/冲突拒答）—— 样式按编码架构规范 5.7/5.8：
 * - 外层由页面 content-card 提供卡片底，内部不再包卡片
 * - 颜色一律 --au-* 变量，禁止硬编码色值；Tag 只用 antd 预设语义色
 * - 提问 → RAG 答案渲染（[n] 引用编号可悬停溯源）→ 拒答/冲突提示
 * - 生效规则徽标（L3-① 规则直查置顶）+ 有用/无用反馈（badcase 回流）
 * - 历史记录：is_stale 过期徽标（L3 失效感知）+ is_cached 缓存命中徽标（P2 结果缓存）
 */
import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Empty, Input, Modal, Popover, Space, Spin, Tag, Tooltip, Typography, message } from 'antd'
import { DislikeOutlined, HistoryOutlined, LikeOutlined } from '@ant-design/icons'
import {
  type KBCitation, type KBQaLog, type KBRuleBrief, askKnowledgeBase, feedbackQaLog, getQaLogs,
} from '@/api/knowledgeBase'

const { Paragraph, Text } = Typography

/** 把答案中的 [n] 渲染为可悬停的引用角标 */
function AnswerText({ answer, citations }: { answer: string; citations: KBCitation[] }) {
  const byNo = new Map(citations.map((c) => [c.no, c]))
  const parts = answer.split(/(\[\d{1,3}\])/g)
  return (
    <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0, color: 'var(--au-slate)' }}>
      {parts.map((p, i) => {
        const m = p.match(/^\[(\d{1,3})\]$/)
        if (!m) return <span key={i}>{p}</span>
        const no = Number(m[1])
        const cite = byNo.get(no)
        if (!cite) return <span key={i}>{p}</span>
        return (
          <Popover key={i} trigger="hover" title={`[${no}] ${cite.doc_name} ${cite.doc_version}${cite.is_current ? '' : '（历史版本）'}`}
            content={<div style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}>{cite.snippet || '（无摘要）'}</div>}>
            <Tag color="processing" style={{ cursor: 'pointer' }}>[{no}]</Tag>
          </Popover>
        )
      })}
    </Paragraph>
  )
}

interface AskResult {
  qa_log_id: number
  answer: string
  citations: KBCitation[]
  rule_entries: KBRuleBrief[]
  is_refused: boolean
  is_conflict: boolean
  cached?: boolean
  latency_ms: number
}

export function ChatPanel({ projectId }: { projectId: number }) {
  const [question, setQuestion] = useState('')
  const [asking, setAsking] = useState(false)
  const [result, setResult] = useState<AskResult | null>(null)
  const [logs, setLogs] = useState<KBQaLog[]>([])
  const [logsTotal, setLogsTotal] = useState(0)
  const [fbOpen, setFbOpen] = useState(false)
  const [fbReason, setFbReason] = useState('')

  const fetchLogs = useCallback(async (page = 1) => {
    if (!projectId) return
    const res = await getQaLogs({ project_id: projectId, page, page_size: 10 })
    setLogs(res.data); setLogsTotal(res.total)
  }, [projectId])

  useEffect(() => { setResult(null); fetchLogs(1) }, [projectId, fetchLogs])

  const handleAsk = async () => {
    const q = question.trim()
    if (!q) return
    setAsking(true)
    try {
      const res = await askKnowledgeBase(projectId, q)
      setResult(res)
      fetchLogs(1)
    } catch { /* request 拦截器已提示 */ } finally {
      setAsking(false)
    }
  }

  const handleFeedback = async (feedback: 1 | 2, reason = '') => {
    if (!result) return
    try {
      await feedbackQaLog(result.qa_log_id, feedback, reason)
      message.success(feedback === 1 ? '感谢反馈' : '已记录，感谢反馈')
      setFbOpen(false); setFbReason('')
      fetchLogs(1)
    } catch { /* request 拦截器已提示 */ }
  }

  if (!projectId) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--au-text-faint)' }}>请先在顶部选择项目</div>

  return (
    <div style={{
      display: 'flex', gap: 16, alignItems: 'stretch', height: '100%', boxSizing: 'border-box',
      padding: '16px 16px 12px 16px', // 与卡片边缘留出空隙（content-card 自身无 padding）
    }}>
      {/* 左侧：提问与答案 */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Space.Compact style={{ width: '100%', flexShrink: 0 }}>
          <Input.TextArea
            value={question} onChange={(e) => setQuestion(e.target.value)}
            placeholder="例如：订单创建的校验规则有哪些？V2.1 的登录流程有什么变化？"
            autoSize={{ minRows: 1, maxRows: 4 }} onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAsk() } }}
            maxLength={1000}
          />
          <Button type="primary" loading={asking} onClick={handleAsk} style={{ height: 'auto' }}>
            提问
          </Button>
        </Space.Compact>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingTop: 16 }}>
          {asking && <div style={{ textAlign: 'center', padding: 40 }}><Spin tip="正在检索知识库并生成答案…" /></div>}
          {!asking && !result && (
            <Empty description="输入问题开始提问，答案会附带文档来源引用" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}
          {!asking && result && (
            <>
              {/* 规则冲突：显式拒答，绝不随机二选一（L2 核心不变式） */}
              {result.is_conflict && (
                <Alert type="warning" showIcon message="规则冲突，已拒绝回答"
                  description="该问题命中的字段存在多条互不一致的规则且尚未人工消解，为避免误导已停止作答。请到「规则管理」页消解冲突后重试。"
                  style={{ marginBottom: 12 }} />
              )}
              {!result.is_conflict && result.is_refused && (
                <Alert type="warning" showIcon message="未找到相关依据" description={result.answer} style={{ marginBottom: 12 }} />
              )}
              {!result.is_refused && <AnswerText answer={result.answer} citations={result.citations} />}

              {/* 生效规则徽标（规则直查置顶注入，与向量引用分开展示） */}
              {result.rule_entries.length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px solid var(--au-border)', paddingTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>生效规则（直查置顶）：</Text>
                  <div style={{ marginTop: 4 }}>
                    {result.rule_entries.map((r) => (
                      <Tooltip key={r.rule_id} title={r.rule_text}>
                        <Tag color="gold" style={{ marginBottom: 4 }}>
                          {r.page_name ? `${r.page_name} · ` : ''}{r.field_name}（{r.doc_version || 'MANUAL'}）
                        </Tag>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>耗时 {result.latency_ms} ms</Text>
                {result.cached && (
                  <Tooltip title="24 小时内相同问题命中结果缓存，直接复用上次答案（文档/规则变更后自动失效）">
                    <Tag color="cyan" style={{ margin: 0 }}>缓存</Tag>
                  </Tooltip>
                )}
                {!result.is_refused && !result.is_conflict && (
                  <Space size={0} style={{ marginLeft: 'auto' }}>
                    <Button type="text" size="small" icon={<LikeOutlined />}
                      onClick={() => handleFeedback(1)}>有用</Button>
                    <Button type="text" size="small" icon={<DislikeOutlined />}
                      onClick={() => setFbOpen(true)}>无用</Button>
                  </Space>
                )}
              </div>

              {!result.is_conflict && result.citations.length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px solid var(--au-border)', paddingTop: 8 }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>引用来源：</Text>
                  <div style={{ marginTop: 4 }}>
                    {result.citations.map((c) => (
                      <Tooltip key={c.no} title={c.snippet}>
                        <Tag color={c.is_current ? 'processing' : 'default'} style={{ marginBottom: 4 }}>
                          [{c.no}] {c.doc_name} {c.doc_version} · {c.section_path || '无章节'}{c.is_current ? '' : '（历史版本）'}
                        </Tag>
                      </Tooltip>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* 右侧：历史记录（窄栏，浅主题底分区） */}
      <div style={{
        width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
        borderLeft: '1px solid var(--au-border)', paddingLeft: 16,
      }}>
        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <HistoryOutlined style={{ color: 'var(--au-primary)' }} />
          <Text strong style={{ color: 'var(--au-slate)' }}>问答历史（{logsTotal}）</Text>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {logs.length === 0 && <Empty description="暂无记录" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          {logs.map((log) => (
            <div key={log.id} style={{
              border: '1px solid var(--au-border)', borderRadius: 8, padding: '8px 12px',
              background: 'var(--au-header-bg)',
            }}>
              <Space size={4} wrap style={{ marginBottom: 4 }}>
                {log.is_refused && (
                  <Tooltip title={log.is_conflict ? '规则冲突未消解，已拒答' : '未找到相关依据，已拒答'}>
                    <Tag color={log.is_conflict ? 'warning' : 'default'} style={{ margin: 0 }}>
                      {log.is_conflict ? '冲突拒答' : '已拒答'}
                    </Tag>
                  </Tooltip>
                )}
                {log.is_stale && (
                  <Tooltip title={log.stale_reason || '所引用文档已被替代/作废'}>
                    <Tag color="warning" style={{ margin: 0 }}>答案已过期</Tag>
                  </Tooltip>
                )}
                {log.is_cached && (
                  <Tooltip title="命中问答缓存（P2 结果缓存），直接复用此前答案">
                    <Tag color="cyan" style={{ margin: 0 }}>缓存</Tag>
                  </Tooltip>
                )}
                {log.feedback === 1 && <Tag color="success" style={{ margin: 0 }}>有用</Tag>}
                {log.feedback === 2 && (
                  <Tooltip title={log.feedback_reason || '用户标记无用'}>
                    <Tag color="error" style={{ margin: 0 }}>无用</Tag>
                  </Tooltip>
                )}
                <Text type="secondary" style={{ fontSize: 12 }}>{log.create_time}</Text>
              </Space>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4, color: 'var(--au-slate)' }}>Q: {log.question}</Text>
              <Paragraph ellipsis={{ rows: 3, expandable: true }} style={{ fontSize: 12, marginBottom: 0, color: 'var(--au-slate-muted)' }}>
                A: {log.answer}
              </Paragraph>
            </div>
          ))}
        </div>
      </div>

      {/* 无用原因弹窗（badcase 回流候选） */}
      <Modal title="标记为无用" open={fbOpen} onCancel={() => setFbOpen(false)}
        onOk={() => handleFeedback(2, fbReason.trim())} okText="提交" cancelText="取消" width={480}
        wrapClassName="aurora-modal-wrap" className="aurora-modal" destroyOnClose>
        <Input.TextArea value={fbReason} onChange={(e) => setFbReason(e.target.value)}
          placeholder="请说明原因（如：答案过时 / 引用了旧版本 / 答非所问 / 编造内容），将作为 badcase 回流候选" autoSize={{ minRows: 3, maxRows: 6 }} maxLength={500} />
      </Modal>
    </div>
  )
}
